/**
 * 日本語のコンテンツ。モード名は `ja.json` のカタログに合わせています。
 * 敬体（です・ます）で統一。修正＝プレイヤーが描き足したもの、絵＝作品、
 * 範囲＝描き直す場所。
 */
import type { LanguageContent } from '../registry.js';

export const JA: LanguageContent = {
  home: {
    intro: [
      'Retusch は 2 人から 10 人で遊ぶオンラインのお絵かきゲームです。ブラウザで動き、インストールもアカウントも要りません。ラウンドごとに、一部が消された絵が配られます。その範囲を描き直して、作品の中に紛れ込ませてください。次は立場が入れ替わり、ほかの人の修正を見つけ出す番です。',
      'たいていのお絵かきゲームは、いちばん早く伝わった人が勝ちます。Retusch はその逆を求めます。勝つとは、気づかれないことです。',
    ],
    steps: [
      {
        title: '穴のあいた絵',
        text: '一部が消されたイラストが配られます。',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: '描き直す',
        text: 'その範囲を、作品に溶け込むように描き直します。',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: '探す',
        text: '全員の修正が入った絵が戻ってきます。ほかの人より先に見つけましょう。',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: '答え合わせ',
        text: '隠し場所がすべて明かされ、点数が決まります。',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'みんなで探す',
        text: '全員が同じ 1 枚の絵に、それぞれ別の範囲を描きます。そのあと全員いっせいに探します。1 つの修正を見つけられるのは、最初に気づいた 1 人だけです。標準のモードで、つねに何かが起きています。',
      },
      {
        name: '配信',
        text: 'ホストは描きません。ほかの全員がホストの絵に隠れ、ホストは 1 人で探します。生配信のために作られたモードで、探す姿そのものが見どころになります。ずっと無料です。',
      },
      {
        name: '1人1枚',
        text: '1 人に 1 枚の作品。誰か 1 人の修正を、たった 1 回のクリックで探します。いちばん単純なルールで、いちばん緊張します。',
      },
      {
        name: '描き手は1人',
        text: '1 ラウンドに描くのは 1 人、残り全員が追いかけます。役割は順番に回り、描いた人は自分の隠し場所が破られるか耐えきるかを見守ります。',
      },
      {
        name: 'バトルロイヤル',
        text: '探し方は同じですが、最初に見つかった修正の作者が脱落します。最後の 1 人まで続き、点数はいっさい数えません。',
      },
      {
        name: 'コンテスト',
        text: '誰も探しません。全員が同じ絵の同じ範囲を描き直し、それぞれがいちばん良いと思った修正に投票します。1 票につき 1 点です。',
      },
    ],
    faq: [
      {
        question: '何人から遊べますか。',
        answer:
          '2 人から 10 人までです。1 人のときは自動の対戦相手が空席に入り、すぐに始められます。4 人を超えたあたりから「みんなで探す」がいちばん面白くなります。',
      },
      {
        question: '無料ですか。',
        answer:
          'はい。すぐ遊ぶ、みんなで探す、配信の 3 つは無料で、時間制限も回数制限もありません。ほかのモードも今のところ無料です。',
      },
      {
        question: 'アカウントは必要ですか。',
        answer:
          'いりません。名前を入れて、押して、遊ぶだけです。アカウントは名前とアイコンを次のゲームに持ち越すためのもので、任意のままです。',
      },
      {
        question: 'スマートフォンでも遊べますか。',
        answer:
          '遊べます。画面はタッチに合わせて変わり、道具の列はたたまれ、指の下に拡大鏡が出て、ダブルタップで寄れます。とはいえ、マウスと大きな画面のほうが細かく描けます。',
      },
      {
        question: '友だちはどうやって誘いますか。',
        answer:
          '部屋を作り、招待リンクをコピーして好きな場所に貼るだけです。友だちはそのまま卓に着きます。アカウントもインストールも要りません。',
      },
      {
        question: '「修正」とは何ですか。',
        answer:
          '消された範囲にあなたが描くもののことです。うまくいけば元の絵の一部だと思われ、誰にも見つかりません。失敗すると、ひと目でわかってしまいます。',
      },
    ],
    headings: {
      intro: '消えることを目指すお絵かきゲーム',
      steps: '遊びかた',
      modes: '6 つのモード',
      faq: 'よくある質問',
      more: 'もっと知る',
    },
    links: {
      rules: 'くわしいルールと点数を読む',
      modes: 'モードをくわしく見くらべる',
    },
  },

  howToPlay: {
    title: 'Retusch の遊びかた',
    summary:
      'Retusch のルールのすべて。1 ラウンドの流れ、隠す側と探す側それぞれの点数、描く道具、そして隠すコツと探すコツ。',
    sections: [
      {
        heading: '3 行でわかる仕組み',
        blocks: [
          '一部が消された絵が配られるので、その範囲を、気づかれないように描き直します。',
          'そのあと全員で、修正された絵を眺め、描き直された範囲を探します。',
          '早く見つければ点になり、自分の隠し場所が耐えるあいだも点になります。',
        ],
      },
      {
        heading: '1 ラウンドの流れ',
        blocks: [
          '1 ゲームは同じラウンドの繰り返しで、標準では 5 ラウンドです。どのラウンドも 3 つの場面を通ります。',
          {
            list: [
              '描く。自分の絵と範囲が配られ、標準では 2 分で描き直します。「完了」は提出の合図です。全員が出した時点で、タイマーを待たずに次へ進みます。',
              '探す。修正の入った絵が戻ってきて、標準では 30 秒で見つけます。描き直された範囲をクリックすると、卓の全員に公開されます。',
              '答え合わせ。誰にも見つからなかったものも含め、すべての隠し場所に枠と名前が付きます。スライダーで、修正後の絵と元の作品を見くらべられます。',
            ],
          },
        ],
      },
      {
        heading: '点数',
        blocks: [
          '2 つの数字が同時に動き、しかも向きが逆です。探す側は早さがほしく、描いた側は自分の範囲に耐えてほしいのです。',
          {
            list: [
              '探す側：最初の 3 秒以内に見つければ 1000 点、そこから一定の割合で下がり、フェーズの終わりで 100 点になります。外した場合も、見つけられなかった場合も 0 点です。',
              '描く側：すぐに破られると 100 点だけ、そこから一定の割合で上がり、最後まで耐えれば 800 点です。誰にも見つからなかった修正は最高の 800 点になります。',
            ],
          },
          '描いた人への加点は答え合わせのときの 1 回だけで、いちばん早く見つけた人を基準にします。ですから 2 人に探されようと 8 人に探されようと同じで、最初に見つけた人だけが効きます。',
          'フェーズを何秒に設定しても点数の仕組みは変わりません。探す時間を延ばしても両端の値は動かず、あいだの傾きが変わるだけです。',
          '1 ラウンドにつき、間違えられるのは 1 回だけです。どの修正にも当たらなかった最初のクリックで、そのラウンドの探索は終わります。このルールがないと、当たるまで画面をやみくもにつつくことになってしまいます。ただし 2 つは無料です。自分の範囲を押した場合と、すでに見つかった修正を押した場合です。',
        ],
      },
      {
        heading: '描く道具',
        blocks: [
          {
            list: [
              'ブラシ（B）：よく使う 3 段階の太さと、細かく決めるスライダー。1・2・3 のキーで 3 段階を選べます。',
              '塗りつぶし（P）：平らな面をひと塗りで埋めます。自分の範囲から外へはみ出すことはありません。',
              'スポイト（I）：絵のどこからでも色を採れます。Alt を押しているあいだだけスポイトになり、離すと元の道具に戻ります。',
              '手のひら（H）：表示を動かします。自分の範囲の外では、ブラシと塗りつぶしも同じ働きをするので、道具を持ち替える場面はほとんどありません。',
              '取り消す（Ctrl+Z または Cmd+Z）：1 ストロークずつ、30 回までさかのぼれます。やり直しはありません。',
              'パレットの端にある下地の色：自分の範囲の最初の色です。これで塗ることが消しゴムの代わりになります。ただし普通のストロークと同じ扱いなので、取り消すことも上から塗ることもできます。だから消しゴムという道具はありません。',
            ],
          },
        ],
      },
      {
        heading: '隠すための 5 つのコツ',
        blocks: [
          {
            list: [
              '色は勘で選ばず、スポイトで採ること。スポイトは周りとまったく同じ色をくれます。少しずれた面は遠目でもわかり、ぴったり合った面は見えなくなります。',
              '範囲を横切っているものは続けること。壁、地平線、片側から入ってきた枝は、反対側の正しい位置から出ていかなければなりません。途切れた線は、慣れた人がまっさきに探す手がかりです。',
              '角から塗ること。いちばん多い失敗です。真ん中は丁寧なのに四隅が最初の下地のままだと、絵を見るまでもなく四角形が目に入ってしまいます。',
              '大きいところから細かいところへ進むこと。まず太いブラシで大きな色の塊を置き、それから細部を詰めます。逆にすると片隅だけで時間を使い切り、残りが空いたままになります。',
              '周りと同じ細かさに合わせること。描き込みの多い場所にある綺麗すぎる範囲は、平らな面にある雑な範囲と同じくらい目立ちます。',
            ],
          },
        ],
      },
      {
        heading: '探すための 4 つのコツ',
        blocks: [
          {
            list: [
              '途切れた線を探すこと。輪郭、ケーブル、髪の毛ほどずれた机の角。たいていそこにあります。',
              'まず全体を見て、それから寄ること。全体表示では、なじんでいない範囲はシミのように見えます。拡大は確かめるためのもので、探すためのものではありません。',
              'なめらかすぎるところを疑うこと。質感のある場所にある完全に均一な面は、雑な塗りと同じくらい怪しいのです。',
              'やみくもに押さないこと。間違えられるのは 1 回だけです。3 秒かけて確かめるほうが、1 ラウンドを失うより安上がりです。',
            ],
          },
        ],
      },
      {
        heading: 'モードによる違いは',
        blocks: [
          'ここまでは標準モードの「みんなで探す」の話です。ほかのモードでは、誰が描くか、誰が探すか、どう勝つかが変わります。それぞれのくわしい説明はモードのページにあります。',
        ],
      },
    ],
  },

  modes: {
    title: 'Retusch のモード',
    summary:
      'Retusch の 6 つの遊びかた。みんなで探す、配信、1人1枚、描き手は1人、バトルロイヤル、コンテスト。流れ、おすすめの人数、所要時間。',
    sections: [
      {
        heading: 'みんなで探す',
        blocks: [
          '標準のモードで、まずここから始めるのがおすすめです。',
          '全員が同じ 1 枚の絵に、それぞれ別の範囲を描きます。修正が出そろうと絵は 1 枚に戻り、全員いっせいに探し始めます。1 つの修正を見つけられるのは、最初に気づいた 1 人だけです。見つかった瞬間に卓の全員へ表示され、以後は対象から外れます。自分のものは決して見つかりませんし、誰にも気づかれなかった隠し場所は作者に最高点をもたらします。',
          '大人数に向いています。待ち時間がなく、全員が同時に探し、誰かの発見がそのまま全員の画面に出ます。',
          'おすすめ：3 人から 10 人。',
        ],
      },
      {
        heading: '配信',
        blocks: [
          '「描き手は1人」を裏返したモードで、生配信のために作られました。',
          'ホスト以外の全員が同じ絵に描きます。そのあと探すのはホストだけで、観客はその様子を見ています。ホストに点は入らず、順位にも並びません。勝負は隠す側どうしで、ホストは進行役です。何度間違えても構いません。1 回だけというルールはホストには適用されません。',
          '配信の夜に向いているのには、はっきりした理由があります。ホストが描かないので、観客はホストの隠し場所ができていく様子をいっさい見ません。ほかのモードではホストが自分の範囲を塗るところが映ってしまい、観客にとって探す面白さが消えてしまいます。',
          'おすすめ：4 人から 10 人。無料で、これからも無料です。',
        ],
      },
      {
        heading: '1人1枚',
        blocks: [
          'プレイヤーはそれぞれ 1 枚の作品を受け取り、そこに自分の修正を隠します。探す番になると、ほかの誰か 1 人の絵だけが渡され、描き直された範囲を 1 回のクリックで指し示します。競争も横取りもありません。自分と、1 枚の絵と、1 つの判断だけです。',
          '2 人で遊ぶときや、それぞれが自分のペースで進める落ち着いたゲームにしたいときに向いています。',
          'おすすめ：2 人から 10 人。',
        ],
      },
      {
        heading: '描き手は1人',
        blocks: [
          '1 ラウンドに描くのは 1 人、残り全員が追いかけます。',
          'ラウンドごとに、絵をもらうのは 1 人だけです。ほかの人は待ってから、その人の修正を探します。採点はそれぞれの自分の時間で行われます。描いた人はいちばん早かった人を基準に採点され、探されているあいだ、止まった自分の隠し場所を見つめることになります。役割は順番に回ります。ここではラウンド数の設定が一巡分を数えます。全員が同じ回数だけ描いてはじめて公平になるからです。',
          '同じ条件で、1 枚ずつ腕くらべをしたい少人数に向いています。',
          'おすすめ：3 人から 6 人。これを超えると待ち時間が長くなります。',
        ],
      },
      {
        heading: 'バトルロイヤル',
        blocks: [
          '「みんなで探す」に、ラウンドごとの脱落を 1 人加えたモードです。',
          '同じ 1 枚の絵、同じ同時進行の探索です。ただしここでは点数をいっさい数えません。問うのは誰が残るか、それだけです。最初に修正を見つけられたプレイヤーがゲームを去ります。ほかの人は最後まで探し続けます。もう誰も脱落しませんが、それぞれにそのラウンドの順位が付き、自分の範囲の上に表示されます。誰も見つからなければ、誰も脱落しません。',
          '夜の締めくくりに向いています。卓が空いていくほど緊張が高まり、最後のラウンドは 2 人になります。',
          'おすすめ：4 人から 10 人。',
        ],
      },
      {
        heading: 'コンテスト',
        blocks: [
          '誰も探さない唯一のモードです。',
          '全員が同じ絵の同じ範囲を描き直します。そのあと各自が自分のペースでほかの人の修正を見て回り、いちばん良いと思ったものに投票します。1 票につき、その作者に 1 点入ります。投票のあいだ作者は伏せられ、自分には投票できません。名前と得票は答え合わせで初めて明かされます。',
          'いろいろな人が混じっているときや、探す時間に追われずに描きたい人がいるときに向いています。',
          'おすすめ：3 人から 10 人。',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'みんなで探す', players: '3 人から 10 人', duration: '15〜20 分' },
    { name: '配信', players: '4 人から 10 人', duration: '15〜20 分' },
    { name: '1人1枚', players: '2 人から 10 人', duration: '15〜20 分' },
    { name: '描き手は1人', players: '3 人から 6 人', duration: '20〜35 分' },
    { name: 'バトルロイヤル', players: '4 人から 10 人', duration: '20〜30 分' },
    { name: 'コンテスト', players: '3 人から 10 人', duration: '10〜15 分' },
  ],
  modeTableHeadings: ['モード', '人数', '所要時間'],
  premium: {
    heading: '無料のモードと有料のモード',
    blocks: [
      '「みんなで探す」と「配信」は、これからもずっと無料です。',
      '残りの 4 つは、ゲームを「作る」ときだけ有料になります。参加はいつまでも無料のままです。つまり卓に 1 人でも解放した人がいれば、全員がすべてのモードで遊べます。アカウントも支払いも要りません。',
      '解放は買い切りで、月額はありません。購入したアカウントでは広告も消えます。それまでは、すべてのモードが誰にでも開かれています。',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Retusch とは',
      date: '2026-08-21',
      summary:
        '複数人で遊ぶオンラインのお絵かきゲーム。かたちは Gartic Phone に近いのに、仕組みはむしろ Meccha Chameleon に似ています。伝えるのではなく、消えることを目指します。',
      sections: [
        {
          heading: '見破られたら負けのお絵かきゲーム',
          blocks: [
            'Retusch は 2 人から 10 人で遊ぶオンラインのお絵かきゲームです。ラウンドごとに、一部が消されたイラストが配られ、その範囲を作品に溶け込むように描き直します。そのあと全員で、修正された絵を眺め、描き直された範囲を探します。',
            '何が違うのかは 1 行で言えます。勝つとは、気づかれないことです。',
          ],
        },
        {
          heading: 'Gartic Phone と共通しているところ',
          blocks: [
            'かたちは同じです。リンクを開き、名前を入れて、遊ぶ。アカウントもインストールも、落としてくるクライアントもありません。ブラウザで動き、数秒で 1 ゲームが始まります。友だちを誘うのも、会話にリンクを貼るだけで卓が埋まります。',
            'テンポも見覚えがあるはずです。短いラウンド、タイマー、描く時間、そして人が何を作ったかがわかる時間。とりわけラウンドの終わりに全員で見て言い合うあの瞬間。あの楽しさはそのままです。',
            'Gartic Phone と同じく、絵がうまい必要はありません。うまく置かれた空のひと塗りは、はみ出した力作に勝ちます。',
          ],
        },
        {
          heading: 'Meccha Chameleon から受け継いだところ',
          blocks: [
            'ただし根っこの仕組みは別のところにあります。Steam の 3D ゲーム Meccha Chameleon では、自分のキャラクターに色を塗り、それを風景の中に置いて、周りと見分けがつかないことを願います。勝敗を決めるのは似せかたと置きかたであって、操作の腕ではありません。',
            'Retusch はその考えかたを、絵の世界に持ち帰りました。隠れる場所は選べず、範囲はゲームから与えられます。そのかわり、そこに入るものはすべて自分で作ります。視線が止まらずに滑っていけば、その隠し場所は成功です。',
            'そして擬態のゲームらしく、緊張は二役から生まれます。あなたは隠れる側であり、同時に探す側です。怪しい塗りを求めて絵を睨んでいるあいだ、誰かがあなたの絵を睨んでいます。',
          ],
        },
        {
          heading: 'ほかにはないところ',
          blocks: [
            '点数は、両者を正反対に評価します。探す側は早く見つけるほど多く、描いた側は範囲が長く耐えるほど多くもらえます。誰にも気づかれなかった修正は、作者に最高点をもたらします。',
            'そこからとても独特な遊びかたが生まれます。目指すのは美しさではなく、正確さです。正しい色を採り、線を正しい位置で続け、角をひとつも空けない。これは描く動作である前に、見る動作です。',
            '手を動かして確かめるなら、くわしいルールを読むか、すぐ遊ぶを押すのがいちばん早いです。設定も待ち時間もなしに始まります。',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Retusch で無敵になるには',
      date: '2026-08-21',
      summary:
        'キーボードのショートカット、大きいところから細かいところへ進む描きかた、そして角を空ける失敗。初心者と、もう誰にも見つけられない人を分ける 3 つのこと。',
      sections: [
        {
          heading: 'ショートカットを覚えると使える時間が倍になります',
          blocks: [
            '道具をいちいちマウスで取りに行くと、2 分はあっという間に溶けます。ショートカットは 1 ラウンドで 20 秒や 30 秒を軽く取り戻しますし、それがそのまま細部に回せる時間になります。',
            {
              list: [
                'B でブラシ、P で塗りつぶし、I でスポイト、H で手のひら。',
                '1・2・3 で 3 段階の太さ。道具の列まで手を伸ばす必要はありません。',
                'Ctrl+Z、Mac なら Cmd+Z で 1 ストロークずつ取り消し。',
                'Alt を押しているあいだだけスポイトになり、離すと元の道具に戻ります。いちばん役に立つのはこれです。描いている途中でも、道具を持ち替えずに色を採れます。',
              ],
            },
            'AZERTY 配列を使っている場合の補足です。キーの表面に &、é、" と書かれていても、1・2・3 は Shift なしで働きます。ゲームが見ているのはキーの位置であって、出てくる文字ではありません。',
          ],
        },
        {
          heading: '大きいところから細かいところへ塗る',
          blocks: [
            '初心者にありがちなのは、片隅から始めて、そこを丁寧に仕上げてしまうことです。時間が切れたときには範囲の 4 分の 3 がまだ下地のままで、修正は 10 メートル先からでもわかります。',
            '正しい順序はその逆です。太いブラシか塗りつぶしから始めて、大きな色の塊を置きます。空、壁、地面の影。20 秒もあれば、範囲全体の印象は正しくなり、もう場違いな四角には見えなくなります。',
            'そこから太さを細くして、境目を作り込みます。横切る地平線、物の輪郭、高さの半ばで変わるグラデーション。時間が足りなくても、失うのは細かさであってラウンドではありません。',
            '色はパレットから選ばず、必ずスポイトで採ってください。だいたいの色味では範囲まるごとが露見しますが、ぴったりの色味はそれを見えなくします。',
          ],
        },
        {
          heading: '角を空けたままにしない',
          blocks: [
            'これが手がかりの筆頭で、慣れた人がまっさきに探すところです。真ん中はよく描けているのに四隅が下地の色のままだと、画面にきれいな長方形が浮かび上がります。絵を見る必要すらありません。図形のかたちが目に飛び込んできます。',
            'まず縁から、それから真ん中という順番を習慣にしてください。よくなじんだ縁は、多少雑な中身を許してくれます。その逆は決して成り立ちません。',
            '縁はわざとはみ出して塗ってください。ストロークは範囲の境目で切り取られるので、大きくはみ出しても何も失いません。角に下地の点がひとつも残らないと確信できる方法は、これだけです。',
          ],
        },
        {
          heading: '探す番が回ってきたら',
          blocks: [
            'まずは拡大せずに絵全体を見渡します。なじんでいない範囲はシミのように見え、拡大は確かめるためだけに使います。それから長い線、絵を横切っている線をたどってください。髪の毛ほどずれた輪郭は、ほぼ確実に修正です。',
            'そして 1 ラウンドに間違えられるのは 1 回だけだということを忘れずに。3 秒の確認は、1 ラウンドより安いのです。',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: '友だちと遊ぶお絵かきゲーム',
      date: '2026-08-21',
      summary:
        'Retusch が少人数でうまく機能する理由、リンクを貼るだけの誘いかた、そして 1 人いれば卓の全員に有料モードが開く仕組み。',
      sections: [
        {
          heading: '誰にも何も求めないゲーム',
          blocks: [
            '離れた場所でゲームをする夜のいちばんの壁は、準備です。作らされるアカウント、落とすクライアント、合わないバージョン。遊び始める前に半分が脱落します。',
            'Retusch はブラウザで動きます。友だちはリンクを押し、名前を入れ、気づけば卓に着いています。入れるものも作るものもなく、パソコンでもスマートフォンでもきちんと動きます。',
            'もうひとつ、大人数のときに効くことがあります。絵が描けないから輪に入れない、ということが起きません。このゲームが求めるのは何かを描き表すことではなく、範囲を周りに合わせることだけです。絵は下手でも、よく見ている人がしばしば勝ちます。',
          ],
        },
        {
          heading: '誘いかたの実際',
          blocks: [
            '最初の画面から部屋を作ります。身内だけで遊ぶなら「公開の部屋」のチェックを外してください。そうすると、リンクの招待だけが入口になります。',
            '専用のボタンでリンクをコピーし、グループのチャットや通話のチャンネル、メッセージアプリに貼ります。部屋のコードが入っているので、友だちはそのままあなたの卓に降りてきます。',
            '人が集まってくるあいだに設定を決められます。モード、ラウンド数、描く時間、範囲の大きさ、絵のカテゴリまで。開始を押すまで何も確定しません。',
          ],
        },
        {
          heading: '1 人が払えば全員が遊べます',
          blocks: [
            '2 つのモードはずっと無料です。標準の「みんなで探す」と、「配信」です。',
            'ほかは有料になりますが、有料になるのはゲームを「作る」ときだけです。参加は無条件で無料のままです。つまり仲間内に 1 人でも解放した人がいれば、卓の全員がすべてのモードを楽しめます。アカウントも支払いも要りません。',
            '解放は買い切りで、月額はありません。',
          ],
        },
        {
          heading: 'この人数ならこのモード',
          blocks: [
            '3 人か 4 人なら「みんなで探す」で十分ですし、1 ラウンドで飲み込めます。6 人を超えると本領を発揮します。発見が次々に続き、画面が止まりません。',
            '夜の締めくくりにはバトルロイヤルを試してください。ラウンドごとに卓が空いていき、最後は 2 人になります。いろいろな人が混じっていて、のんびり描きたい人がいるなら、コンテストが探す時間の圧を丸ごと取り除いてくれます。',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: '配信で遊ぶお絵かきゲーム',
      date: '2026-08-21',
      summary:
        'Retusch には生配信のために作られたモードがあり、配信者が 1 人で探し、視聴者が隠れます。その使いかたと、観客の前で成立するほかのモード。',
      sections: [
        {
          heading: 'そのために作られたモード',
          blocks: [
            '配信モードは役割を逆にします。ホスト以外の全員が同じ絵に描き、ホストはいっさい描きません。そのあと探すのはホストだけで、視聴者はそれを見ています。',
            'これはまさに生配信が必要とする状況で、理由もはっきりしています。ホストが描かないので、ホストの隠し場所は存在せず、チャットは見てはいけないものを何も見ていません。逆に視聴者はほかのプレイヤーの隠し場所を知っていて、ホストがその横を 3 回続けて通り過ぎるのを眺めることになります。',
            'この場面のためにルールが 2 つ書かれています。配信者には点が入らず、順位にも並びません。勝負は隠す側どうしで、配信者は見せ場を回す役です。そして何度でも間違えられます。ほかのモードは最初の 1 回で止まりますが、生配信が不運なクリック 1 回で終わってしまうわけにはいきません。',
            '配信モードは無料で、これからも無料です。',
          ],
        },
        {
          heading: 'コンテストも相性がいい',
          blocks: [
            'コンテストでは、全員が同じ絵の同じ範囲を描き直し、それぞれがいちばん良いと思った修正に投票します。もう探すものはなく、あるのは選ぶことだけです。',
            '観客の前でとてもうまく機能します。理由は配信モードと同じで、探すものがない以上、チャットに壊せるものもありません。作品は並べて見くらべられ、投票が緊張の時間を作り、名前の発表がはっきりした締めになります。いちばん短いモードでもあります。',
          ],
        },
        {
          heading: 'ほかのモードは',
          blocks: [
            '専用モードでなければならない決まりはありませんが、その場合はチャットとではなく友だちと遊ぶのが向いています。ほかのすべてのモードでは配信者が自分の範囲を画面上で塗るので、視聴者はそれができていく様子を見てしまい、探す前から答えを知っています。',
            '逆に、友だち同士のゲームは外から見ていてとても楽しいものです。描く時間には全員が隠し場所を組み立てていく様子が映り、答え合わせではすべての範囲が作者の名前とともに一度に開きます。',
          ],
        },
        {
          heading: 'チャットを卓に入れる',
          blocks: [
            '共有するのは招待リンクだけです。チャットに貼れば、早い人から卓が埋まります。作るアカウントも、ダウンロードも、順番待ちもありません。',
            '人数の上限は部屋で 2 人から 10 人のあいだに設定できます。視聴者が多いときは、長い 1 ゲームより短いゲームをいくつも立てるほうが向いています。1 ラウンドは 3 分もかかりません。',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Retusch の更新',
      date: '2026-08-21',
      summary:
        'Retusch に入ったゲーム面の変更を、新しいものから順に。新しいモード、新しいルール、描く場面と探す場面の改善。',
      sections: [
        {
          heading: '2026 年 8 月',
          blocks: [
            {
              list: [
                '描き直す範囲が白い四角から始まらなくなり、その下にある色のうちいちばん多いものから始まるようになりました。絵の中に存在しない下地を塗り直すことに時間の半分を使わずに済みます。',
                '塗りつぶしがストロークの脇に明るい縁を残さなくなりました。ぼかされた縁の下に回り込むようになっています。',
                'バトルロイヤルが点数を数えなくなりました。最初に見つかった人がゲームを去り、それぞれにそのラウンドの順位が付いて、自分の範囲の上に赤から緑で表示されます。',
                '配信モードでは配信者が何度でも挑戦できるようになりました。最初の失敗でラウンドが終わることはもうありません。',
                '観戦中に、相手が描いている絵を動かしたり拡大したりできるようになりました。',
                '絵文字のリアクションが、画面の下からではなく、プレイヤー一覧にある送った人の名前から飛ぶようになりました。',
                '描き直す範囲の外では、ブラシと塗りつぶしでも表示を動かせます。移動のために手のひらへ持ち替える必要はもうありません。',
                'ゲーム中の左上にあるゲーム名から、確認のうえで最初のページへ戻れるようになりました。',
                '日本語と韓国語が加わり、対応言語は 17 になりました。',
              ],
            },
          ],
        },
        {
          heading: '2026 年 7 月',
          blocks: [
            {
              list: [
                'コンテストを追加しました。全員が同じ範囲を描き直し、それぞれがいちばん良いと思った修正に投票します。',
                '生配信のための配信モードを追加しました。ホストが 1 人で探し、視聴者が隠れます。',
                '難易度に代わって絵のカテゴリを導入しました。見たいものにチェックを入れる方式で、カテゴリごとに見本の絵が出ます。',
                '2 ラウンド続けて同じ絵が出ることがなくなり、チェックしたカテゴリがラウンドごとに入れ替わるようになりました。',
                'ゲーム終了後のギャラリーで、修正をストロークごとに再生し、画像や動く GIF として書き出せるようになりました。',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'ひとことで',
    allArticles: 'すべての記事',
    theRules: 'ルール',
    theModes: 'モード',
  },
};
